import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { IMAGE_URL } from "../../../../utils/api-config";
import { GetCases } from "../../../../api/masters/caseAPI";
import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetAreas } from "../../../../api/masters/areaAPI";
import {
  GetIpdEntryById,
  UpdateIpdEntry
} from "../../../../api/ipd/ipdEntryAPI";
import { GetConsultantSpecializations } from "../../../../api/masters/consultantSpecializationAPI";
import { GetRooms } from "../../../../api/ipd/roomAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  // ===== Visit details =====
  docType: yup.string().required("Document type is required"),
  ipdNo: yup.string().required("IPD number is required"),
  date: yup.date().required("Date is required"),
  time: yup.string().required("Time is required"),
  uhidNo: yup.string().required("UHID number is required"),
  opdNo: yup.string().nullable(),

  // ===== Patient details =====
  patientName: yup.string().required("Patient name is required"),
  sex: yup.string().required("Gender is required"),
  // dob: yup.date().nullable(),
  age: yup.string().required("Age is required"),

  mobileNo: yup
    .string()
    .nullable(),

  // // ===== Doctor info =====
  consultantDoctorId: yup.string().required("Consultant doctor is required"),
  referenceDoctorId: yup.string().required("Referencedoctor is required"),
  departmentId: yup.string().nullable(),
  caseId: yup.string().required("Case is required"),
  roomId: yup.string().required("Room is required"),
  consultantSpecializationId: yup.string().nullable(),

  // ===== Address info =====
  address: yup.string().nullable(),
  // areaId: yup.string().nullable(),

  // ===== Billing info =====
  advance: yup
    .number()
    .required("Advance is required")
    .min(0, "Advance cannot be negative"),
  paymentMode: yup.string().required("Payment mode is required"),
  debitAccount: yup.string().nullable(),

  // ===== Optional fields =====
  email: yup.string().nullable()
});

const EditOpdEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [consultants, setConsultants] = useState([]);
  const [areas, setAreas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cases, setCases] = useState([]);
  const [consultantSpecializations, setConsultantSpecializations] = useState(
    []
  );
  const [rooms, setRooms] = useState([]);
  const [profile, setProfile] = useState("");
  const [profilePreview, setProfilePreview] = useState("");

  const { id } = useParams();

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const handleFileChange = event => {
    const file = event.target.files[0]; // Extract the first file
    if (file) {
      setProfilePreview(URL.createObjectURL(file)); // Set preview
      setProfile(file);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await GetConsultants();
      if (response.success) {
        setConsultants(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await GetAreas();
      if (response.success) {
        setAreas(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await GetDepartments();
      if (response.success) {
        setDepartments(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchConsultantSpecializations = async () => {
    try {
      const response = await GetConsultantSpecializations();
      if (response.success) {
        setConsultantSpecializations(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await GetCases();
      if (response.success) {
        setCases(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await GetRooms();
      if (response.success) {
        setRooms(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchData = async () => {
    try {
      const response = await GetIpdEntryById(id); // Make sure login function returns token
      if (response.success) {
        Object.entries(response.data).forEach(([key, value]) => {
          if (["dob", "date"].includes(key) && value) {
            setValue(key, value.split("T")[0]); // keeps YYYY-MM-DD
          } 
          // Handle the populated Patient Object
          else if (key === "patientId") {
            // Spread patient data into the form (handles name, mobile, etc.)
            Object.entries(value).forEach(([pKey, pValue]) => {
              if (pKey === "dob" && pValue) setValue("dob", pValue.split("T")[0]);
              else if (pKey === "areaId") setValue("areaId", pValue?._id || pValue);
              else setValue(pKey, pValue);
            });
            // Also set the hidden patientId field itself
            setValue("patientId", value._id);
          }
          else {
            setValue(key, value);
          }
        });

        setValue("departmentId", response.data.departmentId?._id);
        setValue("consultantSpecializationId", response.data.consultantSpecializationId?._id);
        setValue("roomId", response.data.roomId?._id);
        setValue("caseId", response.data.caseId?._id);
        setValue("referenceDoctorId", response.data.referenceDoctorId?._id);
        setValue("consultantDoctorId", response.data.consultantDoctorId?._id);

        if (response.data.profile) {
          setProfilePreview(`${IMAGE_URL}${response.data.profile}`);
        }
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  // Fetch existing data
  useEffect(
    () => {
      if (id) {
        fetchConsultants().then(() => {
          fetchAreas().then(() => {
            fetchCases().then(() => {
              fetchDepartments().then(() => {
                fetchRooms().then(() => {
                  fetchConsultantSpecializations().then(() => {
                    fetchData();
                  });
                });
              });
            });
          });
        });
      }
    },
    [id, setValue]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const formData = new FormData();

      for (const key in data) {
        if (data[key] !== "profile") {
          formData.append(key, data[key]);
        }
      }
      if (profile) {
        formData.append("profile", profile);
      }
      const response = await UpdateIpdEntry(id, formData);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
      useKeyboardSubmit(handleSubmit, onSubmit);
      useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="IPD" title="Edit IPD Entry" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Edit IPD Entry</h5>
                <Link to={`/ipd/transaction/ipd-entry/list`}>
                  <button className="btn btn-primary" id="list">
                    List IPD Entry
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List IPD Entry
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    {/* ========================= BASIC INFO ========================= */}
                    <div className="col-12">
                      <h5 className="mt-3 mb-3 text-primary">
                        Basic Information
                      </h5>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Doc Type</label>
                      <input
                        className="form-control"
                        readOnly
                        {...register("docType")}
                      />
                      {errors.docType &&
                        <p className="text-danger">
                          {errors.docType.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>IPD No</label>
                      <input type="number" className="form-control" {...register("ipdNo")} readOnly/>
                      {errors.ipdNo &&
                        <p className="text-danger">
                          {errors.ipdNo.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Date</label>
                      <input
                        type="date"
                        className="form-control"
                        {...register("date")}
                      />
                      {errors.date &&
                        <p className="text-danger">
                          {errors.date.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Time</label>
                      <input
                        type="time"
                        className="form-control"
                        {...register("time")}
                      />
                      {errors.time &&
                        <p className="text-danger">
                          {errors.time.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>UHID No</label>
                      <input className="form-control" {...register("uhidNo")} readOnly/>
                      {errors.uhidNo &&
                        <p className="text-danger">
                          {errors.uhidNo.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Foreigner</label>
                      <select
                        className="form-control"
                        {...register("foreiger")}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                      {errors.foreiger &&
                        <p className="text-danger">
                          {errors.foreiger.message}
                        </p>}
                    </div>

                    {/* ========================= PATIENT DETAILS ========================= */}
                    <div className="col-12">
                      <h5 className="mt-4 mb-3 text-primary">
                        Patient Details
                      </h5>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Patient Name</label>
                      <div className="input-group">
                        <select
                          className="form-select"
                          style={{ maxWidth: "90px" }}
                          {...register("patientNamePrefix")}
                        >
                          <option value="Mr.">Mr.</option>
                          <option value="Mrs.">Mrs.</option>
                          <option value="Ms.">Ms.</option>
                          <option value="Dr.">Dr.</option>
                        </select>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Patient Name"
                          {...register("patientName")}
                        />
                      </div>
                      {errors.patientName &&
                        <p className="text-danger">
                          {errors.patientName.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Mother Name</label>
                      <input
                        className="form-control"
                        {...register("motherName")}
                      />
                      {errors.motherName &&
                        <p className="text-danger">
                          {errors.motherName.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Guardian Name</label>
                      <div className="input-group">
                        <select
                          className="form-select"
                          style={{ maxWidth: "90px" }}
                          {...register("guardianNamePrefix")}
                        >
                          <option value="Mr.">Mr.</option>
                          <option value="Mrs.">Mrs.</option>
                          <option value="Ms.">Ms.</option>
                          <option value="Dr.">Dr.</option>
                        </select>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Guardian Name"
                          {...register("guardianName")}
                        />
                      </div>
                      {errors.guardianName &&
                        <p className="text-danger">
                          {errors.guardianName.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>DOB</label>
                      <input
                        type="date"
                        className="form-control"
                        {...register("dob")}
                      />
                      {errors.dob &&
                        <p className="text-danger">
                          {errors.dob.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Age</label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          {...register("age")}
                        />
                        <select
                          className="form-select"
                          style={{ maxWidth: "100px" }}
                          {...register("ageSuffix")}
                        >
                          <option value="Years">Years</option>
                          <option value="Months">Months</option>
                          <option value="Days">Days</option>
                        </select>
                      </div>
                      {errors.age &&
                        <p className="text-danger">
                          {errors.age.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Sex</label>
                      <select className="form-control" {...register("sex")}>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.sex &&
                        <p className="text-danger">
                          {errors.sex.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Marital Status</label>
                      <select
                        className="form-control"
                        {...register("maritalStatus")}
                      >
                        <option value="Single">Single</option>
                        <option value="Married">Married</option>
                        <option value="Divorced">Divorced</option>
                        <option value="Widowed">Widowed</option>
                      </select>
                      {errors.maritalStatus &&
                        <p className="text-danger">
                          {errors.maritalStatus.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Blood Group</label>
                      <select
                        className="form-control"
                        {...register("bloodGroup")}
                      >
                        <option value="">Select</option>
                        {[
                          "A+",
                          "A-",
                          "B+",
                          "B-",
                          "O+",
                          "O-",
                          "AB+",
                          "AB-"
                        ].map(bg =>
                          <option key={bg} value={bg}>
                            {bg}
                          </option>
                        )}
                      </select>
                      {errors.bloodGroup &&
                        <p className="text-danger">
                          {errors.bloodGroup.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Religion</label>
                      <input
                        className="form-control"
                        {...register("religion")}
                      />
                      {errors.religion &&
                        <p className="text-danger">
                          {errors.religion.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Foreigner</label>
                      <select
                        className="form-control"
                        {...register("foreiger")}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                      {errors.foreiger &&
                        <p className="text-danger">
                          {errors.foreiger.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Occupation</label>
                      <input
                        className="form-control"
                        {...register("occupation")}
                      />
                      {errors.occupation &&
                        <p className="text-danger">
                          {errors.occupation.message}
                        </p>}
                    </div>

                    {/* ========================= CONTACT INFO ========================= */}
                    <div className="col-12">
                      <h5 className="mt-4 mb-3 text-primary">
                        Contact Information
                      </h5>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Address</label>
                      <textarea
                        className="form-control"
                        {...register("address")}
                      />
                      {errors.address &&
                        <p className="text-danger">
                          {errors.address.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Area</label>
                      <select className="form-control" {...register("areaId")}>
                        <option value="">Select Area</option>
                        {areas.map(area =>
                          <option key={area._id} value={area._id}>
                            {area.name}
                          </option>
                        )}
                      </select>
                      {errors.areaId &&
                        <p className="text-danger">
                          {errors.areaId.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Mobile No</label>
                      <input
                        className="form-control"
                        {...register("mobileNo")}
                      />
                      {errors.mobileNo &&
                        <p className="text-danger">
                          {errors.mobileNo.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Phone No</label>
                      <input
                        className="form-control"
                        {...register("phoneNo")}
                      />
                      {errors.phoneNo &&
                        <p className="text-danger">
                          {errors.phoneNo.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        {...register("email")}
                      />
                      {errors.email &&
                        <p className="text-danger">
                          {errors.email.message}
                        </p>}
                    </div>

                    {/* ========================= MEDICAL INFO ========================= */}
                    <div className="col-12">
                      <h5 className="mt-4 mb-3 text-primary">
                        Medical Information
                      </h5>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Room</label>
                      <select className="form-control" {...register("roomId")}>
                        <option value="">Select</option>
                        {rooms.map(room =>
                          <option key={room._id} value={room._id}>
                            {room.roomNo}
                          </option>
                        )}
                      </select>
                      {errors.roomId &&
                        <p className="text-danger">
                          {errors.roomId.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Package</label>
                      <select className="form-control" {...register("package")}>
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                      {errors.package &&
                        <p className="text-danger">
                          {errors.package.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Nature</label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("nature")}
                      />
                      {errors.nature &&
                        <p className="text-danger">
                          {errors.nature.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Case</label>
                      <select className="form-control" {...register("caseId")}>
                        <option value="">Select</option>
                        {cases.map(cas =>
                          <option key={cas._id} value={cas._id}>
                            {cas.name}
                          </option>
                        )}
                      </select>
                      {errors.caseId &&
                        <p className="text-danger">
                          {errors.caseId.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Department</label>
                      <select
                        className="form-control"
                        {...register("departmentId")}
                      >
                        <option value="">Select</option>
                        {departments.map(d =>
                          <option key={d._id} value={d._id}>
                            {d.name}
                          </option>
                        )}
                      </select>
                      {errors.departmentId &&
                        <p className="text-danger">
                          {errors.departmentId.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Reference Doctor</label>
                      <select
                        className="form-control"
                        {...register("referenceDoctorId")}
                      >
                        <option value="">Select</option>
                        {consultants.map(d =>
                          <option key={d._id} value={d._id}>
                            {d.name}
                          </option>
                        )}
                      </select>
                      {errors.referenceDoctorId &&
                        <p className="text-danger">
                          {errors.referenceDoctorId.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Consultant Doctor</label>
                      <select
                        className="form-control"
                        {...register("consultantDoctorId")}
                      >
                        <option value="">Select</option>
                        {consultants.map(d =>
                          <option key={d._id} value={d._id}>
                            {d.name}
                          </option>
                        )}
                      </select>

                      {errors.consultantDoctorId &&
                        <p className="text-danger">
                          {errors.consultantDoctorId.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Consultant Specialization</label>
                      <select
                        className="form-control"
                        {...register("consultantSpecializationId")}
                      >
                        <option value="">Select</option>
                        {consultantSpecializations.map(specialization =>
                          <option
                            key={specialization._id}
                            value={specialization._id}
                          >
                            {specialization.name}
                          </option>
                        )}
                      </select>
                      {errors.consultantSpecializationId &&
                        <p className="text-danger">
                          {errors.consultantSpecializationId.message}
                        </p>}
                    </div>

                    <div className="col-md-12 mb-3">
                      <label>Drug Sensitivity</label>
                      <textarea
                        className="form-control"
                        {...register("drugSensitivity")}
                      />
                      {errors.drugSensitivity &&
                        <p className="text-danger">
                          {errors.drugSensitivity.message}
                        </p>}
                    </div>

                    <div className="col-md-12 mb-3">
                      <label>Family History</label>
                      <textarea
                        className="form-control"
                        {...register("familyHistory")}
                      />
                      {errors.familyHistory &&
                        <p className="text-danger">
                          {errors.familyHistory.message}
                        </p>}
                    </div>

                    <div className="col-md-12 mb-3">
                      <label>Remarks</label>
                      <textarea
                        className="form-control"
                        {...register("remarks")}
                      />
                      {errors.remarks &&
                        <p className="text-danger">
                          {errors.remarks.message}
                        </p>}
                    </div>

                    {/* ========================= BILLING INFO ========================= */}
                    <div className="col-12">
                      <h5 className="mt-4 mb-3 text-primary">
                        Billing Information
                      </h5>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Advance</label>
                      <input
                        type="number"
                        className="form-control"
                        {...register("advance")}
                      />
                      {errors.advance &&
                        <p className="text-danger">
                          {errors.advance.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Payment Mode</label>
                      <select
                        type="number"
                        className="form-control"
                        {...register("paymentMode")}
                      >
                        <option value="">Select</option>
                        <option value="cash">Cash</option>
                        <option value="online">Online</option>
                      </select>
                      {errors.paymentMode &&
                        <p className="text-danger">
                          {errors.paymentMode.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Debit Account</label>
                      <input
                        type="text"
                        className="form-control"
                        {...register("debitAccount")}
                      />
                      {errors.debitAccount &&
                        <p className="text-danger">
                          {errors.debitAccount.message}
                        </p>}
                    </div>

                    {/* ========================= OTHER DETAILS ========================= */}
                    <div className="col-12">
                      <h5 className="mt-4 mb-3 text-primary">Other Details</h5>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>ID Type</label>
                      <input className="form-control" {...register("idType")} />
                      {errors.idType &&
                        <p className="text-danger">
                          {errors.idType.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>ID No</label>
                      <input className="form-control" {...register("idNo")} />
                      {errors.idNo &&
                        <p className="text-danger">
                          {errors.idNo.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Profile</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={handleFileChange}
                      />
                      {errors.profile &&
                        <p className="text-danger">
                          {errors.profile.message}
                        </p>}
                      {profilePreview &&
                        <img
                          src={profilePreview}
                          alt="Preview"
                          width={100}
                          height={100}
                        />}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Expd Discharge Date</label>
                      <input
                        type="date"
                        className="form-control"
                        {...register("expdDischargeDate")}
                      />
                      {errors.expdDischargeDate &&
                        <p className="text-danger">
                          {errors.expdDischargeDate.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Attendent</label>
                      <input
                        className="form-control"
                        {...register("attendent")}
                      />
                      {errors.attendent &&
                        <p className="text-danger">
                          {errors.attendent.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Brought By</label>
                      <input
                        className="form-control"
                        {...register("broughtBy")}
                      />
                      {errors.broughtBy &&
                        <p className="text-danger">
                          {errors.broughtBy.message}
                        </p>}
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditOpdEntry;
